import React, { useState, useEffect, useCallback } from 'react';
import { X, RefreshCw, Folder, FolderOpen, File, AlertCircle } from 'lucide-react';
import { useOrganization, useClerk } from '@clerk/clerk-react';
import { getApiBaseUrl, getOrgSlug } from '../config/backend.config';
import './WorkspaceFilesModal.css';

function WorkspaceFilesModal({ isOpen, onClose, selectedWorkspace }) {
  const { organization } = useOrganization();
  const clerk = useClerk();
  const [fileStructure, setFileStructure] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set());

  const fetchFileStructure = useCallback(async () => {
    if (!clerk?.session || !organization) {
      setError('Authentication required');
      return;
    }

    if (!selectedWorkspace) {
      setError('Workspace is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        setError('Unable to get authentication token');
        return;
      }

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const response = await fetch(`${apiBaseUrl}/workspaces/${selectedWorkspace.workspace_id}/file-structure`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch file structure: ${response.statusText}`);
      }

      const data = await response.json();
      setFileStructure(data);
    } catch (err) {
      console.error('Error fetching file structure:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [clerk, organization, selectedWorkspace]);

  // Only fetch when workspace changes, not on every open
  useEffect(() => {
    if (isOpen && selectedWorkspace && clerk?.session && !fileStructure) {
      fetchFileStructure();
    }
  }, [isOpen, selectedWorkspace, clerk, fetchFileStructure, fileStructure]);

  const handleRefresh = () => {
    fetchFileStructure();
  };

  const toggleFolder = (path) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedFolders(newExpanded);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const renderFileTree = (items, depth = 0) => {
    if (!items || items.length === 0) return null;

    return items.map((item, index) => {
      const isExpanded = expandedFolders.has(item.path);
      const paddingLeft = `${depth * 20 + 10}px`;

      if (item.type === 'directory') {
        return (
          <div key={`${item.path}-${index}`} className="file-tree-item">
            <div
              className="file-tree-folder"
              style={{ paddingLeft }}
              onClick={() => toggleFolder(item.path)}
            >
              {isExpanded ? <FolderOpen size={16} /> : <Folder size={16} />}
              <span className="file-tree-name">{item.name}</span>
              <span className="file-tree-count">({item.children?.length || 0})</span>
            </div>
            {isExpanded && item.children && (
              <div className="file-tree-children">
                {renderFileTree(item.children, depth + 1)}
              </div>
            )}
          </div>
        );
      } else {
        return (
          <div
            key={`${item.path}-${index}`}
            className="file-tree-item"
            style={{ paddingLeft }}
          >
            <div className="file-tree-file">
              <File size={16} />
              <span className="file-tree-name">{item.name}</span>
              <span className="file-tree-size">{formatFileSize(item.size)}</span>
            </div>
          </div>
        );
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div className="workspace-files-modal-overlay" onClick={onClose}>
      <div className="workspace-files-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="workspace-files-modal-header">
          <div className="workspace-files-modal-title">
            <Folder size={16} />
            <span>Workspace Files</span>
          </div>
          <button className="workspace-files-modal-close" onClick={onClose} title="Close">
            <X size={16} />
          </button>
        </div>

        {/* Controls */}
        <div className="workspace-files-modal-controls">
          <button
            className="workspace-files-refresh-button"
            onClick={handleRefresh}
            disabled={loading}
            title="Refresh file structure"
          >
            <RefreshCw size={12} className={loading ? 'spinning' : ''} />
            <span>Refresh</span>
          </button>
        </div>

        {/* Content */}
        <div className="workspace-files-modal-content">
          {loading ? (
            <div className="workspace-files-loading">
              <RefreshCw size={24} className="spinning" />
              <span>Loading workspace files...</span>
            </div>
          ) : error ? (
            <div className="workspace-files-error">
              <AlertCircle size={24} />
              <span>Error loading files: {error}</span>
              <button onClick={handleRefresh} className="workspace-files-retry-button">
                Try Again
              </button>
            </div>
          ) : !fileStructure || !fileStructure.file_structure ? (
            <div className="workspace-files-empty">
              <Folder size={48} />
              <span>No file structure found</span>
              <p>The workspace may be empty or inaccessible</p>
            </div>
          ) : (
            <div className="file-tree-container">
              <div className="file-tree">
                {renderFileTree(fileStructure.file_structure)}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="workspace-files-modal-footer">
          <div className="workspace-files-footer-info">
            {fileStructure && fileStructure.file_structure && (
              <span>
                {fileStructure.file_structure.length} items in workspace root
              </span>
            )}
            {selectedWorkspace && (
              <span className="workspace-files-footer-workspace">
                Workspace: {selectedWorkspace.workspace_name}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default WorkspaceFilesModal;
